/**
 * CTASection — Bottom call-to-action section
 * Design: Navy/gradient background with green CTA button
 */

import React from "react";
import { ArrowRight, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";

const benefits = [
  "No Upfront Fees",
  "No Credit Impact on Prequalification",
  "Results in Under 2 Minutes",
];

export default function CTASection() {
  return (
    <section data-testid="cta-section" className="py-20 md:py-28 bg-gradient-to-r from-[#0f2d52] via-[#1a4a7a] to-[#0f2d52] relative overflow-hidden">
      {/* Triangle background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-5">
        <svg viewBox="0 0 800 400" className="absolute w-full h-full">
          <polygon points="400,20 780,380 20,380" stroke="#48d480" strokeWidth="2" fill="none" />
          <polygon points="400,50 740,360 60,360" stroke="#48d480" strokeWidth="1.5" fill="none" />
          <polygon points="400,80 700,340 100,340" stroke="#48d480" strokeWidth="1" fill="none" />
        </svg>
      </div>

      <div className="container relative z-10 text-center max-w-3xl mx-auto">
        {/* Tagline badge */}
        <div className="inline-flex items-center gap-2 bg-[#48d480]/20 border border-[#48d480]/30 rounded-full px-4 py-1.5 mb-6">
          <span className="w-2 h-2 rounded-full bg-[#48d480] animate-pulse" />
          <span style={{ fontFamily: "'Inter', sans-serif" }} className="text-[#48d480] text-xs font-semibold tracking-wide">
            Ready to Get Started?
          </span>
        </div>

        {/* Headline */}
        <h2
          style={{ fontFamily: "'Montserrat', sans-serif" }}
          className="text-3xl md:text-5xl font-black text-white leading-tight mb-6"
        >
          Take the First Step Toward
          <br />
          <span className="text-[#48d480]">Business Growth</span>
        </h2>

        {/* Subheadline */}
        <p
          style={{ fontFamily: "'Inter', sans-serif" }}
          className="text-white/70 text-lg leading-relaxed mb-8 max-w-xl mx-auto"
        >
          Join thousands of business owners who have secured funding with Legacy Triangle. Your success story starts here.
        </p>

        {/* Benefits */}
        <div className="flex flex-wrap justify-center gap-4 mb-10">
          {benefits.map((benefit) => (
            <div key={benefit} className="flex items-center gap-2">
              <CheckCircle size={16} className="text-[#48d480]" />
              <span
                style={{ fontFamily: "'Inter', sans-serif" }}
                className="text-white/80 text-sm font-medium"
              >
                {benefit}
              </span>
            </div>
          ))}
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Link
            to="/apply"
            data-testid="cta-apply-btn"
            className="flex items-center justify-center gap-2 text-base px-8 py-4 bg-[#e8821c] hover:bg-[#d67518] text-white font-bold rounded-lg transition-colors shadow-lg"
          >
            Apply Now <ArrowRight size={18} />
          </Link>
          <Link
            to="/contact"
            className="btn-outline-white flex items-center justify-center gap-2 text-base px-8 py-4"
          >
            Schedule Free Consultation
          </Link>
        </div>

        {/* Trust line */}
        <p
          style={{ fontFamily: "'Inter', sans-serif" }}
          className="text-white/40 text-xs mt-8"
        >
          Trusted by 10,000+ businesses nationwide. BBB Accredited. SSL Secured.
        </p>
      </div>
    </section>
  );
}
